import i18next from "i18next"
import { mapKeys } from "lodash"

/**
 * Request object for copying events from synced calendars to the general calendar
 * 
 * @typedef Request
 * @property {String|Array<String>} calendarIds - The calendar feed ID(s) to copy events from
 * @property {String} communityId - The community ID
 * @property {Boolean} reEnableSync - Re-enable auto-sync after import (default: true)
 */

/**
 * @typedef ValidationError
 * @property {Boolean} isValid
 * @property {Array} messages
 */

class PostCopyEventsToGeneralRequest {

    /**
     * @param {Request} request
     */
    constructor({
        calendarIds,
        communityId,
        reEnableSync = false,
    }) {
        // Support both single ID and array of IDs
        this.calendarIds = Array.isArray(calendarIds) ? calendarIds : [calendarIds]
        this.communityId = communityId
        this.reEnableSync = reEnableSync
    }

    /**
     * Post data ready for usage by the API
     * 
     * @returns {Request}
     */
    getPayload = () => {
        return {
            "calendar_feed_ids": this.calendarIds,
            "community_id": this.communityId,
            "re_enable_sync": this.reEnableSync,
        }
    }

    /**
     * Determine error messages from the given data if any
     * 
     * @returns {ValidationError}
     */
    validator = () => {
        const errors = []

        if (!this.calendarIds || this.calendarIds.length === 0) {
            errors.push({
                field: "calendarIds",
                isValid: false,
                message: i18next.t("calendar.detail.copyEvents.error.requiredCalendarId")
            })
        }

        if (!this.communityId) {
            errors.push({
                field: "communityId",
                isValid: false,
                message: i18next.t("calendar.detail.copyEvents.error.requiredCommunityId")
            })
        }

        return {
            isValid: (errors.length === 0),
            messages: mapKeys(errors, error => error.field)
        }
    }
}

export default PostCopyEventsToGeneralRequest
